import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { PS, T } from '../lib/theme';
import { ToastContext, type ToastMessage, type ToastKind } from '../hooks/useToast';

const COLORS: Record<ToastKind, { bg: string; border: string; text: string }> = {
  info:    { bg: PS.infoBg,    border: PS.infoBorder,    text: PS.infoText },
  success: { bg: PS.successSoft, border: PS.success,     text: PS.success },
  error:   { bg: PS.errorBg,   border: PS.errorBorder,   text: PS.errorText },
};

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastMessage[]>([]);
  // Track pending auto-dismiss timers so we can cancel them on dismiss/unmount.
  // Without this, a timer scheduled by mount A keeps firing after StrictMode's
  // dev-mode unmount/remount, dismissing a toast on mount B that it never created.
  const timersRef = useRef<Map<number, ReturnType<typeof setTimeout>>>(new Map());

  const dismiss = useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer !== undefined) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((curr) => curr.filter((t) => t.id !== id));
  }, []);

  const push = useCallback(
    (kind: ToastKind, text: string) => {
      const id = nextId++;
      setToasts((curr) => [...curr, { id, kind, text }]);
      const timer = setTimeout(() => dismiss(id), 4000);
      timersRef.current.set(id, timer);
    },
    [dismiss],
  );

  // Clear any pending timers on unmount (StrictMode dev double-invoke, real
  // teardown, etc.) so stray fires don't try to update an unmounted provider.
  useEffect(() => {
    const timers = timersRef.current;
    return () => {
      timers.forEach((t) => clearTimeout(t));
      timers.clear();
    };
  }, []);

  const value = useMemo(() => ({ push, toasts, dismiss }), [push, toasts, dismiss]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div
        style={{
          position: 'fixed',
          top: 16,
          right: 16,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          zIndex: 1000,
        }}
      >
        {toasts.map((t) => {
          const c = COLORS[t.kind];
          return (
            <div
              key={t.id}
              onClick={() => dismiss(t.id)}
              style={{
                background: c.bg,
                border: `1px solid ${c.border}`,
                color: c.text,
                padding: '10px 14px',
                borderRadius: 4,
                cursor: 'pointer',
                maxWidth: 360,
                ...T.ui,
              }}
            >
              {t.text}
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}
